// Rechte in der Oberfläche auswerten – reine Funktionen über der Sitzungsantwort (docs/API.md §3.1).
// Das ist Anzeige-Logik: Sie blendet aus, was ohnehin abgelehnt würde. Maßgeblich prüft der Server.
import {
  PERMISSION_AUTHENTICATED,
  PERMISSION_PUBLIC,
  type AnyActionContract,
  type PermissionKey,
} from '@shared/contract'
import { moduleManifests } from '@shared/contracts.gen'
import type { SessionLocation, SessionPayload } from './types'

/**
 * Für welchen Standort ein Recht gelten soll:
 *   Standort-ID  genau dort (mandantenweite Rechte zählen mit)
 *   'any'        an irgendeinem Standort – für Navigation und Listen, die selbst nach Standorten filtern
 *   'all'        für alle Standorte – nur mandantenweite Rechte zählen (so prüft der Server ohne Standortangabe)
 *   'active'     am Arbeitsstandort; ist „Alle Standorte“ gewählt, wie 'any' (Standard von usePermission)
 */
export type PermissionScope = string | 'any' | 'all' | 'active'

type Access = Pick<SessionPayload, 'permissions' | 'activeLocationId'>

const includes = (list: readonly string[] | undefined, key: string): boolean =>
  list !== undefined && (list.includes(key) || list.includes('*'))

export function hasPermission(
  session: Access | null | undefined,
  key: PermissionKey | null | undefined,
  scope: PermissionScope | null = 'active',
): boolean {
  if (key === undefined || key === null || key === '' || key === PERMISSION_PUBLIC) return true
  if (!session) return false
  if (key === PERMISSION_AUTHENTICATED) return true
  const { all, byLocation } = session.permissions
  if (includes(all, key)) return true
  const where = scope === null || scope === 'active' ? (session.activeLocationId ?? 'any') : scope
  if (where === 'all') return false
  if (where === 'any') return Object.values(byLocation).some((keys) => includes(keys, key))
  return includes(byLocation[where], key)
}

/** Standorte, an denen das Recht gilt (alle sichtbaren, wenn es mandantenweit gilt). */
export function locationsWithPermission(
  session: Pick<SessionPayload, 'permissions' | 'locations'> | null | undefined,
  key: PermissionKey,
): SessionLocation[] {
  if (!session) return []
  if (key === PERMISSION_AUTHENTICATED || key === PERMISSION_PUBLIC || includes(session.permissions.all, key))
    return session.locations
  return session.locations.filter((location) => includes(session.permissions.byLocation[location.id], key))
}

/** Ist der Funktionsschalter an – bei mehreren: alle? „!schalter“ verlangt, dass er AUS ist. Ohne Angabe: ja. */
export const hasFeature = (session: Pick<SessionPayload, 'tenant'> | null | undefined, key?: string | readonly string[] | null): boolean =>
  !key ||
  (typeof key === 'string' ? [key] : key).every((entry) =>
    entry.startsWith('!') ? !session?.tenant?.features.includes(entry.slice(1)) : Boolean(session?.tenant?.features.includes(entry)),
  )

/** Darf der Nutzer die Aktion derzeit überhaupt aufrufen? (`session.actions` + Recht am Standort) */
export function canRunAction(
  session: (Access & Pick<SessionPayload, 'actions' | 'tenant'>) | null | undefined,
  contract: AnyActionContract,
  scope: PermissionScope = 'active',
): boolean {
  if (!session) return false
  if (!session.actions.includes(contract.name)) return false
  return hasFeature(session, contract.feature) && hasPermission(session, contract.permission, scope)
}

/** Sichtbar, wenn Recht (an irgendeinem Standort) und Funktionsschalter passen – für Navigation, Befehle, Widgets. */
export const passesGate = (
  session: (Access & Pick<SessionPayload, 'tenant'>) | null | undefined,
  gate: { permission?: PermissionKey; feature?: string | readonly string[] },
  scope: PermissionScope = 'any',
): boolean => hasFeature(session, gate.feature) && hasPermission(session, gate.permission, scope)

let labels: Map<string, string> | null = null

/** Deutscher Name eines Rechts aus dem Rechtekatalog der Module: „Für ‚Audit-Protokoll lesen‘ fehlt Ihnen das Recht.“ */
export function permissionLabel(key: PermissionKey): string {
  if (!labels) {
    labels = new Map()
    for (const manifest of moduleManifests)
      for (const permission of manifest.permissions) labels.set(permission.key, permission.label)
  }
  return labels.get(key) ?? key
}

/** Arbeitsorte für den Standort-Schalter: virtuelle Standorte (Transit, Kommission, Verleih) sind keine. */
export const WORK_LOCATION_KINDS = ['warehouse', 'store', 'vehicle'] as const

export const isWorkLocation = (location: Pick<SessionLocation, 'kind'>): boolean =>
  (WORK_LOCATION_KINDS as readonly string[]).includes(location.kind)

export const LOCATION_KIND_LABELS: Record<string, string> = {
  warehouse: 'Lager',
  store: 'Filialen',
  vehicle: 'Fahrzeuge',
  transit: 'Transit',
  consignment: 'Kommission',
  rental_out: 'Verleih',
}
